import { Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  getDocs,
  writeBatch,
  Timestamp,
  doc,
  updateDoc,
  limit,
  type Firestore,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { FirebaseService } from './firebase.service';

export interface FirestoreGroup {
  firestore_id: string;
  name: string;
  members: Array<{ id?: number; name?: string; username?: string; avatar?: string }>;
  created_by: number;
  created_at?: string;
  academy_id?: number;
}

export interface FirestoreGroupFilter {
  academyId?: number;
  memberUserIds?: number[];
}

export interface FirestoreMessage {
  firestore_id: string;
  content: string;
  message?: string;
  sender_id: any;
  sender_name: string;
  created_by?: string;
  created_at?: string;
  chat_id?: string;
  read?: boolean;
  type?: 'text' | 'image' | 'file';
  file_url?: string;
  file_name?: string;
}

export interface FirestoreChatStatistics {
  total: number;
  replied: number;
  unread: number;
  not_replied: number;
}

@Injectable({ providedIn: 'root' })
export class FirestoreChatService implements OnDestroy {
  private db: Firestore;
  private listeners: Array<() => void> = [];

  constructor(
    private ngZone: NgZone,
    private firebaseService: FirebaseService,
  ) {
    this.db = this.firebaseService.firestore;
    const projectId = this.firebaseService.firebaseApp?.options?.projectId || 'UNKNOWN';
    console.warn(`[FirestoreChat 🔍] Connected to Firebase Project ID: ${projectId}`);
  }

  refreshForCurrentAcademy(): void {
    this.firebaseService.refreshForCurrentAcademy();
    this.db = this.firebaseService.firestore;
    console.warn(`[FirestoreChat 🔄] Active Project ID: ${this.firebaseService.firebaseApp?.options?.projectId}`);
  }

  /**
   * Listen to new messages in a chat (real-time via Firestore).
   * Uses chat_id string (e.g. "ac7_pa44") as Firestore collection path.
   * Only fires callback for messages created AFTER `afterTimestamp`.
   */
  listenToChat(
    chatId: string,
    afterTimestamp: number,
    onNewMessage: (msg: FirestoreMessage) => void
  ): () => void {
    const messagesRef = collection(this.db, `chats/${chatId}/messages`);
    const q = query(messagesRef, orderBy('created_at', 'asc'));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        this.ngZone.run(() => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
              const data = change.doc.data();
              const msgTime = data['created_at']?.toMillis?.() || 0;

              if (msgTime <= afterTimestamp) return;

              const textContent = data['message'] ?? data['content'] ?? '';
              onNewMessage({
                firestore_id: change.doc.id,
                content: textContent,
                message: textContent,
                sender_id: data['sender_id'],
                sender_name: data['sender_name'],
                created_by: data['created_by'],
                created_at: data['created_at']?.toDate?.()?.toISOString(),
                chat_id: data['chat_id'],
              });
            }
          });
        });
      },
      (error) => {
        console.warn('Firestore listener error:', error);
      }
    );

    this.listeners.push(unsub);
    return unsub;
  }

  /**
   * Write a message to Firestore for real-time delivery.
   */
  async sendMessage(
    chatId: string,
    msg: {
      content: string;
      sender_id: any;
      sender_name: string;
      created_by?: string;
      type?: 'text' | 'image' | 'file';
      file_url?: string;
      file_name?: string;
    }
  ): Promise<void> {
    console.log(`[FirestoreChat 🚀] Sending message to chatId: ${chatId}`, msg);
    try {
      const messagesRef = collection(this.db, `chats/${chatId}/messages`);
      const payload: any = {
        message: msg.content,
        content: msg.content,
        sender_id: String(msg.sender_id),
        sender_name: msg.sender_name,
        created_by: msg.created_by ?? '',
        type: msg.type ?? 'text',
        chat_id: chatId,
        read: false,
        created_at: Timestamp.now(),
      };
      if (msg.file_url) payload.file_url = msg.file_url;
      if (msg.file_name) payload.file_name = msg.file_name;

      const docRef = await addDoc(messagesRef, payload);
      console.log(`[FirestoreChat ✅] Message sent successfully! MessageDoc ID: ${docRef.id}`);
    } catch (error) {
      console.error(`[FirestoreChat ❌] Error sending message to chatId: ${chatId}`, error);
      throw error;
    }
  }

  /**
   * Load all messages from a Firestore chat (for initial load).
   */
  loadMessages(
    chatId: string,
    onMessages: (msgs: FirestoreMessage[]) => void
  ): () => void {
    console.log(`[FirestoreChat 📡] Subscribing to messages for chatId: ${chatId}`);
    const messagesRef = collection(this.db, `chats/${chatId}/messages`);
    const q = query(messagesRef, orderBy('created_at', 'asc'));
    let subcollectionMessages: FirestoreMessage[] = [];
    let summaryMessage: FirestoreMessage | null = null;

    const emitMessages = (): void => {
      const messages = [...subcollectionMessages];
      if (summaryMessage && !messages.some((message) =>
        message.content === summaryMessage?.content
        && message.created_at === summaryMessage?.created_at
      )) {
        messages.push(summaryMessage);
      }
      messages.sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')));
      this.ngZone.run(() => onMessages(messages));
    };

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        console.log(`[FirestoreChat 📩] Snapshot received for chatId: ${chatId} (${snapshot.docs.length} messages)`);
        subcollectionMessages = snapshot.docs.map((doc) => {
            const data = doc.data();
            const textContent = data['message'] ?? data['content'] ?? '';
            return {
              firestore_id: doc.id,
              content: textContent,
              message: textContent,
              sender_id: data['sender_id'],
              sender_name: data['sender_name'] ?? '',
              created_by: data['created_by'] ?? '',
              created_at: data['created_at']?.toDate?.()?.toISOString(),
              chat_id: data['chat_id'],
              read: data['read'] ?? false,
              type: data['type'] ?? 'text',
              file_url: data['file_url'],
              file_name: data['file_name'],
            };
          });
        emitMessages();
      },
      (error) => {
        console.error(`[FirestoreChat ❌] Firestore load error for chatId: ${chatId}`, error);
      }
    );

    this.listeners.push(unsub);

    // Mobile clients currently update the parent chat document's
    // last_message fields. Read that legacy summary as a fallback so mobile
    // messages remain visible until the mobile app writes the subcollection.
    const summaryUnsub = onSnapshot(
      doc(this.db, 'chats', chatId),
      (snapshot) => {
        const data = snapshot.data();
        const textContent = data?.['last_message'];
        if (!textContent) {
          summaryMessage = null;
          emitMessages();
          return;
        }

        const timestamp = data?.['last_message_time'];
        summaryMessage = {
          firestore_id: `summary-${chatId}`,
          content: String(textContent),
          message: String(textContent),
          sender_id: data?.['sender_id'] ?? data?.['created_by'] ?? '',
          sender_name: data?.['sender_name'] ?? data?.['created_by'] ?? '',
          created_at: timestamp?.toDate?.()?.toISOString?.() ?? new Date().toISOString(),
          chat_id: chatId,
          read: Boolean(data?.['read'] ?? false),
          type: 'text',
        };
        emitMessages();
      },
      (error) => console.warn(`[FirestoreChat ⚠️] Summary listener error for chatId: ${chatId}`, error),
    );
    this.listeners.push(summaryUnsub);

    return () => {
      unsub();
      summaryUnsub();
    };
  }

  /**
   * Mark all messages from the other user as read.
   */
  async markAsRead(chatId: string, currentUserId: number): Promise<void> {
    try {
      const messagesRef = collection(this.db, `chats/${chatId}/messages`);
      const q = query(messagesRef, where('read', '==', false));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return;

      const batch = writeBatch(this.db);
      let count = 0;
      snapshot.docs.forEach((d) => {
        if (d.data()['sender_id'] !== currentUserId) {
          batch.update(d.ref, { read: true });
          count++;
        }
      });
      if (count > 0) await batch.commit();
    } catch (e) {
      console.warn('markAsRead error:', e);
    }
  }

  /**
   * Get unread message count for a chat (messages NOT from currentUserId that are unread).
   */
  async getUnreadCount(chatId: string, currentUserId: number): Promise<number> {
    try {
      const messagesRef = collection(this.db, `chats/${chatId}/messages`);
      const q = query(messagesRef, where('read', '==', false));
      const snapshot = await getDocs(q);
      let count = 0;
      snapshot.docs.forEach((d) => {
        if (d.data()['sender_id'] !== currentUserId) count++;
      });
      return count;
    } catch (e) {
      return 0;
    }
  }

  /**
   * Build the dashboard counters from the same Firestore messages displayed by
   * the chats page. Only incoming messages are classified, so every message is
   * counted once as replied, unread, or read without a later reply.
   */
  async getChatStatistics(chatIds: string[], currentUserId: number): Promise<FirestoreChatStatistics> {
    const statistics: FirestoreChatStatistics = {
      total: 0,
      replied: 0,
      unread: 0,
      not_replied: 0,
    };
    const uniqueChatIds = [...new Set(chatIds.filter(Boolean))];
    const batchSize = 10;

    for (let offset = 0; offset < uniqueChatIds.length; offset += batchSize) {
      const batchIds = uniqueChatIds.slice(offset, offset + batchSize);
      const chats = await Promise.all(batchIds.map((chatId) => this.getMessagesOnce(chatId)));

      chats.forEach((messages) => {
        let hasLaterOutgoingMessage = false;

        for (let index = messages.length - 1; index >= 0; index--) {
          const message = messages[index];

          if (message.sender_id === currentUserId) {
            hasLaterOutgoingMessage = true;
            continue;
          }

          statistics.total++;
          if (hasLaterOutgoingMessage) {
            statistics.replied++;
          } else if (!message.read) {
            statistics.unread++;
          } else {
            statistics.not_replied++;
          }
        }
      });
    }

    return statistics;
  }

  /** Get all group chat IDs shown by the chats page. */
  async getGroupChatIds(): Promise<string[]> {
    const snapshot = await getDocs(collection(this.db, 'chat_groups'));
    return snapshot.docs.map((group) => `group_${group.id}`);
  }

  private async getMessagesOnce(chatId: string): Promise<FirestoreMessage[]> {
    const messagesRef = collection(this.db, `chats/${chatId}/messages`);
    const snapshot = await getDocs(query(messagesRef, orderBy('created_at', 'asc')));

    return snapshot.docs.map((message) => ({
      firestore_id: message.id,
      content: message.data()['content'] ?? '',
      sender_id: Number(message.data()['sender_id']),
      sender_name: message.data()['sender_name'] ?? '',
      created_at: message.data()['created_at']?.toDate?.()?.toISOString(),
      chat_id: message.data()['chat_id'],
      read: message.data()['read'] ?? false,
      type: message.data()['type'] ?? 'text',
      file_url: message.data()['file_url'],
      file_name: message.data()['file_name'],
    }));
  }

  /**
   * Save a new group to Firestore.
   */
  async createGroup(
    name: string,
    members: Array<{ id?: number; name?: string; username?: string; avatar?: string }>,
    createdBy: number,
    academyId?: number,
  ): Promise<string> {
    console.log(`[FirestoreChat 👥] Creating new chat group: "${name}"`, { members, createdBy, academyId });
    try {
      const groupsRef = collection(this.db, 'chat_groups');
      const docRef = await addDoc(groupsRef, {
        name,
        members,
        created_by: createdBy,
        academy_id: academyId ?? null,
        created_at: Timestamp.now(),
      });
      console.log(`[FirestoreChat ✅] Group created successfully with ID: ${docRef.id}`);
      return docRef.id;
    } catch (error) {
      console.error(`[FirestoreChat ❌] Error creating group "${name}"`, error);
      throw error;
    }
  }

  /**
   * Listen to all chat groups in real-time.
   */
  loadGroups(
    onGroups: (groups: FirestoreGroup[]) => void,
    filter?: FirestoreGroupFilter,
  ): () => void {
    const groupsRef = collection(this.db, 'chat_groups');
    const q = query(groupsRef, orderBy('created_at', 'desc'));

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        console.log(`[FirestoreChat 👥] Loaded ${snapshot.docs.length} chat groups from Firestore`);
        this.ngZone.run(() => {
          const groups: FirestoreGroup[] = snapshot.docs
            .map((doc) => ({
              firestore_id: doc.id,
              name: doc.data()['name'],
              members: doc.data()['members'] || [],
              created_by: doc.data()['created_by'],
              academy_id: Number(doc.data()['academy_id'] ?? 0) || undefined,
              created_at: doc.data()['created_at']?.toDate?.()?.toISOString(),
            }))
            .filter((group) => this.matchesGroupFilter(group, filter));
          onGroups(groups);
        });
      },
      (error) => {
        console.warn('Firestore groups listener error:', error);
      }
    );

    this.listeners.push(unsub);
    return unsub;
  }

  /**
   * Upload a file to Firebase Storage and return the download URL.
   */
  async uploadFile(file: File, chatId: string): Promise<string> {
    console.log(`[FirestoreChat 📁] Uploading file "${file.name}" to chatId: ${chatId}`);
    try {
      const storage = getStorage(this.firebaseService.firebaseApp);
      const fileRef = storageRef(storage, `chats/${chatId}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      console.log(`[FirestoreChat ✅] File uploaded successfully! URL: ${downloadUrl}`);
      return downloadUrl;
    } catch (error) {
      console.error(`[FirestoreChat ❌] Error uploading file "${file.name}"`, error);
      throw error;
    }
  }

  /**
   * Real-time sidebar summary for a chat: fires callback with the latest
   * message preview and unread count every time messages change.
   */
  listenChatSummary(
    chatId: string,
    currentUserId: number,
    callback: (summary: {
      content: string;
      created_at: string;
      unreadCount: number;
      senderName: string;
      senderId: string;
    }) => void
  ): () => void {
    const messagesRef = collection(this.db, `chats/${chatId}/messages`);
    const q = query(messagesRef, orderBy('created_at', 'asc'));

    const unsub = onSnapshot(q, (snapshot) => {
      this.ngZone.run(() => {
        const docs = snapshot.docs;
        if (docs.length === 0) return;

        const lastDoc = docs[docs.length - 1].data();
        const rawContent: string = lastDoc['content'] ?? lastDoc['message'] ?? '';
        const msgType: string = lastDoc['type'] ?? 'text';
        const content = rawContent ||
          (msgType === 'image' ? '📷 صورة' :
            msgType === 'file' ? `📎 ${lastDoc['file_name'] ?? 'ملف'}` : '');
        const created_at = lastDoc['created_at']?.toDate?.()?.toISOString() ?? '';

        let unreadCount = 0;
        const currentUserKey = String(currentUserId);
        let senderName = '';
        let senderId = '';

        for (let index = docs.length - 1; index >= 0; index--) {
          const data = docs[index].data();
          const docSenderId = String(data['sender_id'] ?? '');
          const isIncoming = docSenderId !== '' && docSenderId !== currentUserKey;

          if (!data['read'] && isIncoming) {
            unreadCount++;
            if (!senderName) {
              senderId = docSenderId;
              senderName = this.resolveMessageSenderName(data);
            }
          }
        }

        if (!senderName) {
          senderId = String(lastDoc['sender_id'] ?? '');
          senderName = this.resolveMessageSenderName(lastDoc);
        }

        callback({ content, created_at, unreadCount, senderName, senderId });
      });
    }, () => { });

    this.listeners.push(unsub);
    return unsub;
  }

  private matchesGroupFilter(group: FirestoreGroup, filter?: FirestoreGroupFilter): boolean {
    if (!filter?.academyId) {
      return true;
    }

    if (group.academy_id && Number(group.academy_id) === filter.academyId) {
      return true;
    }

    const memberIds = (filter.memberUserIds ?? []).filter((id) => id > 0);
    if (memberIds.length === 0) {
      return !group.academy_id;
    }

    return (group.members || []).some((member) => memberIds.includes(Number(member.id)));
  }

  private resolveMessageSenderName(data: Record<string, unknown>): string {
    const candidates = [
      data['sender_name'],
      data['senderName'],
      data['from_name'],
      data['fromName'],
      data['created_by_name'],
    ];

    for (const candidate of candidates) {
      const value = String(candidate ?? '').trim();
      if (value) {
        return value;
      }
    }

    return '';
  }

  /**
   * Get the last message for a chat (for sidebar preview).
   */
  async getLastMessage(chatId: string): Promise<{ content: string; created_at: string } | null> {
    try {
      const messagesRef = collection(this.db, `chats/${chatId}/messages`);
      const q = query(messagesRef, orderBy('created_at', 'desc'), limit(1));
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      const d = snapshot.docs[0].data();
      return {
        content: d['content'] ?? '',
        created_at: d['created_at']?.toDate?.()?.toISOString() ?? '',
      };
    } catch {
      return null;
    }
  }

  /**
   * Update a group's fields (name, members, etc.) in Firestore.
   */
  async updateGroup(
    firestoreId: string,
    updates: Partial<{ name: string; members: any[] }>
  ): Promise<void> {
    const docRef = doc(this.db, 'chat_groups', firestoreId);
    await updateDoc(docRef, updates as any);
  }

  /**
   * Cleanup all active listeners.
   */
  unsubscribeAll(): void {
    this.listeners.forEach((fn) => fn());
    this.listeners = [];
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }
}
